/**
 * Utility functions for validating date ranges against place information
 * These functions can be reused for DateSuggest components that need to validate
 * dates against standardized place date ranges.
 */

/**
 * Parses PlaceSuggest yearRange string (e.g. "1927 - Present", "1927 - 2000") into fromYear/toYear.
 * @param {string} yearRange - e.g. "1927 - Present", "1927 - 2000", "1927"
 * @returns {{ fromYear: number|null, toYear: number|null }}
 */
export function parseYearRangeFromPlaceSuggest(yearRange) {
  if (!yearRange || typeof yearRange !== 'string') {
    return { fromYear: null, toYear: null }
  }
  const trimmed = yearRange.trim()
  if (!trimmed) return { fromYear: null, toYear: null }

  const present = /present/i
  const yearNum = /\d{4}/

  const dashRegex = /[-–—]/
  const dashMatch = dashRegex.exec(trimmed)
  const dashIdx = dashMatch ? dashMatch.index : -1
  const parts = dashIdx === -1 ? [trimmed] : [trimmed.slice(0, dashIdx).trim(), trimmed.slice(dashIdx + 1).trim()]

  const fromMatch = parts[0] != null ? yearNum.exec(parts[0]) : null
  const fromYear = fromMatch ? Number(fromMatch[0]) : null
  if (parts.length === 1) {
    return { fromYear, toYear: null }
  }
  const toPart = parts[1]
  const toMatch = toPart != null ? yearNum.exec(toPart) : null
  if (present.test(toPart) && !toMatch) {
    return { fromYear, toYear: new Date().getUTCFullYear() }
  }
  const toYear = toMatch ? Number(toMatch[0]) : null
  return { fromYear, toYear }
}

/**
 * Extracts years from a formal date text (gedcomX format)
 * Handles formats like: +1900/+2000, +1900, A+1900, /+1900, +1900/, +1900-01-01/+2000-12-31
 * @param {string} formalText - The formal date text
 * @returns {number[]} Array of years extracted from the date
 */
export function extractYearsFromFormalText(formalText) {
  if (!formalText) return []

  const years = []

  // Handle date ranges like +1900/+2000 or +1900-01-01/+2000-12-31
  if (formalText.includes('/')) {
    const [fromPart, toPart] = formalText.split('/')

    // Extract year from "from" part (handles +1900, A+1900, /+1900, +1900-01-01)
    // Match pattern: [+-] followed by digits (the year)
    const fromMatch = /([+-]\d+)/.exec(fromPart)
    if (fromMatch) {
      years.push(Number(fromMatch[1]))
    }

    // Extract year from "to" part (handles +2000, +2000/, +2000-12-31)
    if (toPart) {
      const toMatch = /([+-]\d+)/.exec(toPart)
      if (toMatch) {
        years.push(Number(toMatch[1]))
      }
    }
  } else {
    // Single year like +1900, A+1900, or +1900-01-01
    // Match pattern: [+-] followed by digits (the year)
    const match = /([+-]\d+)/.exec(formalText)
    if (match) {
      years.push(Number(match[1]))
    }
  }

  return years
}

/**
 * Validates if dates are within placeInformation range
 * @param {Object} dateValue - The date value object with gedcomX property
 * @param {Object} placeInfo - Place information object with fromYear and toYear
 * @returns {Object|null} - Returns null if valid, { allOutside: true } if entire range is outside,
 *                          { partiallyOutside: true } if partially outside
 */
export function validateDateRangeAgainstPlace(dateValue, placeInfo) {
  if (!placeInfo || !dateValue?.gedcomX) return null

  const formalText = dateValue.gedcomX
  const years = extractYearsFromFormalText(formalText)
  if (!years || years.length === 0) return null

  const { fromYear, toYear } = placeInfo
  if (fromYear === null && toYear === null) return null

  // Check if all years are outside the placeInformation range
  const allYearsOutside = years.every((year) => {
    if (fromYear !== null && toYear !== null) {
      return year < fromYear || year > toYear
    }
    if (fromYear !== null) return year < fromYear
    if (toYear !== null) return year > toYear
    return false
  })

  if (allYearsOutside) {
    return { allOutside: true }
  }

  // Check if any year is outside the placeInformation range
  const anyYearOutside = years.some((year) => {
    if (fromYear !== null && year < fromYear) return true
    if (toYear !== null && year > toYear) return true
    return false
  })

  if (anyYearOutside) {
    return { partiallyOutside: true }
  }

  return null
}

/**
 * Gets place information for a specific placeId from group's placeInformation array
 * @param {Array} placeInformation - Array of place information objects
 * @param {string} placeId - The place ID to find
 * @returns {Object|null} - Place information object or null if not found
 */
export function getPlaceInfoById(placeInformation, placeId) {
  if (!placeInformation || !placeId) return null
  return placeInformation.find((info) => info?.repId === placeId) || null
}

/**
 * Gets the place's date range (fromYear, toYear) from current coverages when the place was just selected
 * and not yet in group.placeInformation.
 * @param {Array} coverages - Current coverages array (each may have place with fromYear, toYear)
 * @param {string} placeId - The place ID to find
 * @returns {Object|null} - { repId, fromYear, toYear } for date validation, or null
 */
export function getPlaceDateRangeFromCoverages(coverages, placeId) {
  if (!coverages?.length || !placeId) return null
  const coverage = coverages.find((c) => String(c?.place?.id) === String(placeId))
  const p = coverage?.place
  if (!p || (p.fromYear == null && p.toYear == null)) return null
  return {
    repId: String(placeId),
    fromYear: p.fromYear ?? null,
    toYear: p.toYear ?? null,
  }
}
